<template>
    <app-layout>
        <div>
            <page-header :title="trans('assets.activation.headline')">
                <p class="mb-0">{{ trans('assets.activation.description') }}</p>
                <filter-bar
                    :config="filterBarConfig"
                    :query-params="activeQueryParams"
                    :search-placeholder="trans('assets.activation.filter.placeholder')"
                    class="mt-3"
                >
                    <template #locations-option="{ option }">
                        <div class="flex items-center justify-between gap-3">
                            <span>{{ option.label }}</span>
                            <small class="text-muted">{{ option.iataCode }}</small>
                        </div>
                    </template>
                    <template #types-option="{ option }">
                        <div class="flex items-center justify-between gap-3">
                            <span>{{ option.label }}</span>
                            <small class="text-muted">{{ option.shortLabel }}</small>
                        </div>
                    </template>
                </filter-bar>
            </page-header>
            <single-column-table-view v-if="isMobile" :fields="fields" :items="transformedAssets" @row-clicked="showDetails">
                <template #header-row="{ item }">
                    <div class="d-flex justify-content-end w-100">
                        <button class="btn btn-secondary btn-icon" type="button" @click.stop="openQrCodeScanner(item)">
                            <icon name="cil-qr-code" style="shape-rendering: crispEdges" />
                        </button>
                    </div>
                </template>
            </single-column-table-view>
            <div v-else>
                <data-table
                    :actions="actions"
                    :columns="fields"
                    :items="transformedAssets"
                    :no-items-view="noItemsView"
                    :query-params="activeQueryParams"
                    @row-clicked="showDetails"
                >
                    <template #designation="{ item }">
                        <td>
                            <div>
                                {{ item.designation }}
                            </div>
                            <small class="text-dark text-nowrap">{{ trans('assets.overview.cell.serial_number') }}: {{ item.serialNumber }}</small>
                        </td>
                    </template>

                    <template #type="{ item }">
                        <td>
                            {{ item.type }}
                        </td>
                    </template>

                    <template #location="{ item }">
                        <td>
                            <span>
                                {{ item.location.iataCode }}
                            </span>
                        </td>
                    </template>

                    <template #specific_location="{ item }">
                        <specific-location-overview-cell :location="item.location" />
                    </template>

                    <template #area="{ item }">
                        <area-location-overview-cell :location="item.location" />
                    </template>

                    <template #manufacturer="{ item }">
                        <td>
                            {{ item.manufacturer }}
                        </td>
                    </template>
                </data-table>
            </div>
        </div>
    </app-layout>
</template>
<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import FilterBar from '@/Components/FilterBar.vue';
import DataTable from '@/Components/DataTable.vue';
import { useCurieIdScanner } from '@/composables/useCurieIdScanner.ts';
import PageHeader from '@/Components/PageHeader.vue';
import SingleColumnTableView from '@/Pages/SingleColumnTableView.vue';
import SpecificLocationOverviewCell from '@/Components/SpecificLocationOverviewCell.vue';
import AreaLocationOverviewCell from '@/Components/AreaLocationOverviewCell.vue';
import useQueryParams from '@/Utils/UseQueryParams';
import { isMobile } from '@/Utils/UseMedia';
import { when } from '@/Utils/when';
import { computed, onBeforeUnmount, onMounted } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { trans } from '@/mixins/i18n.js';
import { route } from 'ziggy-js';

const activeQueryParams = useQueryParams({
    paramsWithDefaults: {
        search: null,
        usages: [],
        operabilities: [],
        types: [],
        locations: [],
        specificLocations: [],
        sort: { column: 'date', asc: false },
    },
    route: route('assets.activation'),
    options: {
        only: ['assets'],
    },
});

const filterBarConfig = computed(() => {
    return {
        route: route('assets.activation'),
        routeOptions: { only: ['assets'] },
        defaultSort: { column: 'type', asc: true },
        filters: [
            {
                type: 'search',
                key: 'search',
                placeholder: trans('assets.overview.filter.placeholder'),
            },
            {
                type: 'multi-select',
                key: 'locations',
                label: trans('dashboard.filter.locations'),
                options: locations.value,
                showSearchbar: true,
            },
            {
                type: 'multi-select',
                key: 'specificLocations',
                label: trans('activities.overview.cell.specific_location'),
                options: specificLocations.value,
                showSearchbar: true,
            },
            {
                type: 'multi-select',
                key: 'types',
                label: trans('assets.overview.filter.type'),
                options: types.value,
            },
        ],
    };
});

const page = usePage();
const assets = computed(() => page.props.assets);
const locations = computed(() => page.props.filters.locations);
const specificLocations = computed(() => (page.props.filters.specificLocations?.data || []).map((loc) => ({ label: loc.name, value: loc.name })));
const types = computed(() => page.props.filters.types?.data || []);

const noItemsView = computed(() => ({
    noItems: trans('assets.overview.results.empty'),
}));

const fields = computed(() =>
    [
        {
            key: 'type',
            label: trans('assets.overview.cell.type'),
            _style: 'width: 90px',
        },
        {
            key: 'designation',
            label: trans('assets.overview.cell.designation'),
            _style: 'width: 20%',
        },
        when(locations.value?.data?.length > 1, {
            key: 'location',
            label: trans('assets.overview.cell.airport'),
            _style: 'width: 90px',
        }),
        {
            key: 'specific_location',
            label: trans('assets.overview.cell.location'),
            _style: 'width: 15%;white-space: nowrap;',
        },
        when(assets.value?.meta?.hasAreaLocation, {
            key: 'area',
            label: trans('locations.area.label'),
            _style: 'width: 15%;white-space: nowrap;',
        }),
        {
            key: 'manufacturer',
            label: trans('assets.overview.cell.manufacturer'),
        },
    ].filter(Boolean),
);

const transformedAssets = computed(() => {
    if (!assets.value) {
        return null;
    }
    const notAvailable = trans('general.not_available');
    const { meta, links, data } = assets.value;

    return {
        meta,
        links,
        data: data.map((asset) => ({
            id: asset.id,
            type: asset.type.shortLabel,
            designation: asset.designation ? asset.designation.label : notAvailable,
            manufacturer: asset.manufacturer ? asset.manufacturer.name : notAvailable,
            curieId: asset.curieId,
            serialNumber: asset.serialNumber || notAvailable,
            location: asset.location,
        })),
    };
});

function showDetails(asset) {
    router.visit(route('assets.show', asset.id));
}

const { openForAttachingToAsset } = useCurieIdScanner();

function openQrCodeScanner(item) {
    openForAttachingToAsset(item.id);
}

function startCurieIdScannerIfNecessary() {
    const parts = window.location.hash.split('#assign-curie-id=');

    if (parts.length === 2) {
        openQrCodeScanner({ id: parts[1] });
    }
}

onMounted(() => {
    window.addEventListener('hashchange', startCurieIdScannerIfNecessary);
});

onBeforeUnmount(() => {
    window.removeEventListener('hashchange', startCurieIdScannerIfNecessary);
});

const actions = computed(() => [
    {
        permissions: ['asset.update'],
        label: trans('assets.overview.scanner.title'),
        type: 'primary',
        click: openQrCodeScanner,
    },
]);
</script>
